import { Injectable } from '@angular/core';
import { Observable, switchMap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CaqRequestService } from '../caq-request/caq-request.service';
import {
  Estado,
  Cidade,
  PlanoEducacional,
  LocalizacaoSelecionada,
  InformacoesLocalizacaoSelecionada,
  Escola
} from '../../interfaces/location.interface';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { CHAVES_STORAGE } from '../../constants/storage-keys';
import { LayoutResultadoService } from "../../layouts/layout-resultado/layout-resultado.service";

@Injectable({
  providedIn: 'root'
})
export class LocalizacaoService {
  private apiUrl = environment.apiUrl;

  constructor(private caqRequestService: CaqRequestService,
              private layoutResultadoService: LayoutResultadoService) { }

  // Busca todos os estados da API

  buscarEstados(): Observable<Estado[]> {
    const url = `${this.apiUrl}/estados?size=27`;

    return this.caqRequestService.get<any>(url).pipe(
      map(response => {
        const estados: Estado[] = [];
        if (response?._embedded?.estados) {
          for (const estado of response._embedded.estados) {
            estados.push({
              value: estado.codUf.toString(),
              label: estado.nomeUf.toUpperCase(),
              sigla: estado.siglaUf.toString()
            });
          }
        }
        return estados.sort((a, b) => a.label > b.label ? 1 : -1);
      })
    );
  }

  // Busca cidades de um estado específico

  buscarCidades(estado?: Estado): Observable<Cidade[]> {
    const url = estado
      ? `${this.apiUrl}/municipios/search/findMunicipioByCodUf?codUf=${estado.value}`
      : `${this.apiUrl}/municipios`;

    return this.caqRequestService.get<any>(url).pipe(
      map(response => {
        const cidades: Cidade[] = [];
        if (response?._embedded?.municipios) {
          for (const municipio of response._embedded.municipios) {
            // Não exibe Fernando de Noronha (lógica já aplicada no front antigo)
            if (municipio.codMun.toString() !== '2605459') {
              cidades.push({
                value: municipio.codMun.toString(),
                label: municipio.nomeMun.toUpperCase()
              });
            }
          }
        }
        return cidades.sort((a, b) => a.label > b.label ? 1 : -1);
      })
    );
  }

  // Salva a localização selecionada no sessionStorage

  salvarLocalizacaoSelecionada(localizacao: LocalizacaoSelecionada): void {
    sessionStorage.setItem(CHAVES_STORAGE.SELECIONAR_LOCALIZACAO, JSON.stringify(localizacao));
  }

  // Salva o tipo de simulação no sessionStorage

  salvarTipoSimulacao(tipo: number): void {
    sessionStorage.setItem(CHAVES_STORAGE.TIPO_SIMULACAO, tipo.toString());
  }

  // Recupera a localização selecionada do sessionStorage

  obterLocalizacaoSelecionada(): LocalizacaoSelecionada | null {
    const dados = sessionStorage.getItem(CHAVES_STORAGE.SELECIONAR_LOCALIZACAO);
    return dados ? JSON.parse(dados) : null;
  }

  // Recupera o tipo de simulação do sessionStorage

  obterTipoSimulacao(): number | null {
    const tipo = sessionStorage.getItem(CHAVES_STORAGE.TIPO_SIMULACAO);
    return tipo ? parseInt(tipo, 10) : null;
  }

  // Limpa os dados de localização

  limparLocalizacao(): void {
    sessionStorage.removeItem(CHAVES_STORAGE.SELECIONAR_LOCALIZACAO);
    sessionStorage.removeItem(CHAVES_STORAGE.TIPO_SIMULACAO);

    this.layoutResultadoService.setCidade('');
    this.layoutResultadoService.setEstado('');
    this.layoutResultadoService.setPais('');
  }


  //Obtém as opções de plano educacional baseado na localização selecionada

  obterOpcoesPlanoEducacional(localizacao: LocalizacaoSelecionada, ehR4: boolean = false, ehR3: boolean = false, ehReceitasDespesas: boolean = false): PlanoEducacional[] {
    const opcoes: PlanoEducacional[] = [];

    if (localizacao.cidadeSelecionada) {
      // Para cidades no R3: apenas Plano da Rede Municipal
      // Para cidades em R2 e R4: Plano Municipal e Plano da Rede Municipal
      if (!ehR3 && !ehReceitasDespesas) {
        opcoes.push({
          value: TipoSimulacao.planejamento,
          label: 'Plano municipal de educação',
        });
      }

      opcoes.push({
        value: TipoSimulacao.planejamentoPorEsferaAdmMunicipal,
        label: 'Plano da rede municipal de educação',
      });
    } else if (localizacao.estadoSelecionado) {
      // Para estados: Plano Estadual e Plano da Rede Estadual
      opcoes.push({
        value: TipoSimulacao.planejamento,
        label: 'Plano estadual de educação'
      });

      opcoes.push({
        value: TipoSimulacao.planejamentoPorEsferaAdmEstadual,
        label: 'Plano da rede estadual de educação'
      });
    } else {
      // Nível nacional
      opcoes.push({
        value: TipoSimulacao.planejamentoNacional,
        label: 'Plano nacional de educação'
      });
    }

    return opcoes;
  }

  // Busca informações complementares da localização

  buscarInformacoesLocalizacao(localizacao: LocalizacaoSelecionada): Observable<InformacoesLocalizacaoSelecionada> {
    const localidadeSelecionada = this.obterStringLocalidade(localizacao.estadoSelecionado, localizacao.cidadeSelecionada);
    const url = `${this.apiUrl}/informacoes-socio-demograficas/${localidadeSelecionada}`;

    return this.caqRequestService.get<any>(url).pipe(
      map(response => {
        return {
          dadosSociodemograficos: response?.content ? [response.content] : [],
          pibPerCapitaPorNivel: response?.content?.pibPerCapita,
          idhmPorNivel: response?.content?.valorIdh
        } as InformacoesLocalizacaoSelecionada;
      })
    );
  }

  // Determina o tipo de localidade para as URLs da API (nacional/estado/municipio)

  private obterStringLocalidade(estado?: Estado, cidade?: Cidade): string {
    if (cidade) {
      return `municipio/${cidade.value}`;
    }
    if (estado) {
      return `estado/${estado.value}`;
    }
    return 'nacional';
  }


  // Valida se a localização está completa para navegação

  validarLocalizacaoCompleta(localizacao: LocalizacaoSelecionada): boolean {
    // Precisa ter plano educacional selecionado
    return !!localizacao.planoEducacionalSelecionado && !!localizacao.tipoSimulacaoSelecionado;
  }

  // Gera nome da localização selecionada

  obterNomeLocalizacao(localizacao: LocalizacaoSelecionada): string {
    if (localizacao.cidadeSelecionada) {
      return `${localizacao.cidadeSelecionada.label} - ${localizacao.estadoSelecionado?.label}`;
    }
    if (localizacao.estadoSelecionado) {
      return localizacao.estadoSelecionado.label;
    }
    return 'Brasil';
  }

  // Salva a escola selecionada (atualiza a localização existente)

  salvarEscolaSelecionada(escola: Escola | null): void {
    const localizacao = this.obterLocalizacaoSelecionada();
    if (localizacao) {
      localizacao.escolaSelecionada = escola || undefined;
      this.salvarLocalizacaoSelecionada(localizacao);
    }
  }

  // Obtém a escola selecionada

  obterEscolaSelecionada(): Escola | null {
    const localizacao = this.obterLocalizacaoSelecionada();
    return localizacao?.escolaSelecionada || null;
  }

  // Limpa apenas a escola selecionada

  limparEscolaSelecionada(): void {
    const localizacao = this.obterLocalizacaoSelecionada();
    if (localizacao) {
      delete localizacao.escolaSelecionada;
      this.salvarLocalizacaoSelecionada(localizacao);
    }
  }

  ehLocalizacaoMunicipal(): boolean {
    const localizacao = this.obterLocalizacaoSelecionada();
    return !!localizacao?.cidadeSelecionada;
  }
}
